import { cn } from "~/lib/utils";

interface Props {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    description?: string;
    disabled?: boolean;
}

/**
 * A generic toggle component for table filtering
 */
export function TableToggle(props: Props) {
    const {
        checked,
        onChange,
        label,
        description = null,
        disabled = false,
    } = props;

    const handleToggle = () => {
        if (disabled) return;
        onChange(!checked);
    };

    return (
        <div
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(event) => {
                if (disabled) return;

                if (event.key === "Enter" || event.key === " ") {
                    handleToggle();
                }
            }}
            className={cn(
                "flex flex-col cursor-pointer overflow-hidden group select-none",
                disabled && "opacity-50 cursor-not-allowed",
            )}
            aria-label={label}
        >
            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={handleToggle}
                    className={cn(
                        "h-4 w-4 text-primary focus:ring-primary border rounded-sm text-xs transition-colors group-hover:border-primary",
                        {
                            "cursor-not-allowed pointer-events-none": disabled,
                        },
                    )}
                />
                <span
                    className={cn(
                        "text-foreground whitespace-nowrap transition-colors",
                        {
                            "text-xs": description !== null,
                            "text-sm": description === null,
                            "group-hover:text-foreground": !disabled,
                        },
                    )}
                >
                    {label}
                </span>
            </div>
            {description && (
                <p
                    className={cn(
                        "text-slate-600 text-xs whitespace-nowrap transition-colors",
                        {
                            "group-hover:text-slate-700": !disabled,
                        },
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    );
}
